// client/src/components/leagues/OpponentInfoCard.js
import React from 'react'
import { Link } from 'react-router-dom'
import PropTypes from 'prop-types'

/**
 * OpponentInfoCard - Displays opponent details including contact info,
 * bio, and league statistics
 */
export default function OpponentInfoCard({
  matchData,
  currentUserId,
  opponentDetails,
  opponentStats,
  isLoadingStats,
  leagueId,
  opponentPlayerId,
}) {
  // Don't render if we don't have match data
  if (!matchData || !currentUserId) {
    return null
  }

  // Don't render loading state, just return null to avoid IIFE
  if (!opponentDetails) {
    return null
  }

  const opponent = opponentDetails

  return (
    <div className="opponent-info-card mb-4">
      <div className="card">
        <div className="card-header bg-primary text-white">
          <h5 className="card-title mb-0">
            <span className="me-2">👤</span>
            Your Opponent
          </h5>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-6">
              <h6 className="fw-bold">
                {opponentPlayerId ? (
                  <Link
                    to={`/players/${opponentPlayerId}/history`}
                    state={{
                      fromLeague: true,
                      leagueId: Number(leagueId),
                      divisionId: matchData?.division_id,
                    }}
                    className="text-decoration-none"
                    title={`View ${opponent.name}'s match history`}
                  >
                    {opponent.name}
                  </Link>
                ) : (
                  opponent.name
                )}
              </h6>

              {/* Contact Information */}
              <div className="contact-info mt-3">
                {opponent.email && (
                  <div className="mb-2">
                    <small className="text-muted">Email:</small>
                    <br />
                    <a
                      href={`mailto:${opponent.email}`}
                      className="text-primary"
                    >
                      {opponent.email}
                    </a>
                  </div>
                )}
                {opponent.phone && (
                  <div className="mb-2">
                    <small className="text-muted">Phone:</small>
                    <br />
                    <a href={`tel:${opponent.phone}`} className="text-primary">
                      {opponent.phone}
                    </a>
                  </div>
                )}
                {!opponent.email && !opponent.phone && (
                  <small className="text-muted">
                    No contact information available
                  </small>
                )}
              </div>
            </div>

            <div className="col-md-6">
              {/* Opponent Stats */}
              <div className="opponent-stats">
                <h6 className="fw-bold mb-3">League Statistics</h6>
                {isLoadingStats ? (
                  <div className="text-muted small">Loading stats...</div>
                ) : opponentStats ? (
                  <div>
                    <div className="mb-3">
                      <small className="text-muted">
                        Sets: {opponentStats.setsWon}-{opponentStats.setsLost} |
                        Games: {opponentStats.gamesWon}-
                        {opponentStats.gamesLost}
                      </small>
                    </div>
                    <div className="stats-card-grid">
                      <div className="stat-card">
                        <div className="stat-card-value text-success">
                          {opponentStats.wins}
                        </div>
                        <div className="stat-card-label">Wins</div>
                      </div>
                      <div className="stat-card">
                        <div className="stat-card-value text-danger">
                          {opponentStats.losses}
                        </div>
                        <div className="stat-card-label">Losses</div>
                      </div>
                      <div className="stat-card">
                        <div className="stat-card-value text-primary">
                          {opponentStats.winPercentage}%
                        </div>
                        <div className="stat-card-label">Win Rate</div>
                      </div>
                      <div className="stat-card">
                        <div className="stat-card-value">
                          {opponentStats.totalMatches}
                        </div>
                        <div className="stat-card-label">Matches</div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <small className="text-muted">No statistics available</small>
                )}
              </div>
            </div>
          </div>

          {/* Bio section */}
          {opponent.bio && (
            <div className="mt-3 pt-3 border-top">
              <small className="text-muted">About:</small>
              <p className="mb-0 small">{opponent.bio}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

OpponentInfoCard.propTypes = {
  matchData: PropTypes.shape({
    division_id: PropTypes.number,
  }),
  currentUserId: PropTypes.number,
  opponentDetails: PropTypes.shape({
    name: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
    bio: PropTypes.string,
  }),
  opponentStats: PropTypes.shape({
    wins: PropTypes.number,
    losses: PropTypes.number,
    winPercentage: PropTypes.number,
    totalMatches: PropTypes.number,
    setsWon: PropTypes.number,
    setsLost: PropTypes.number,
    gamesWon: PropTypes.number,
    gamesLost: PropTypes.number,
  }),
  isLoadingStats: PropTypes.bool.isRequired,
  leagueId: PropTypes.string.isRequired,
  opponentPlayerId: PropTypes.number,
}
